import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges,
  ViewChild,
} from '@angular/core';
import { environment } from '../../../../environments/environment';

/*
 * La escena de la puesta en marcha — la que Juan vio y aprobó el 2026-09-28: «A · Constelación» en su
 * modo simple (`scratchpad/escena/escena-aprobada.html`, objeto `AC` con `SIMPLE=true`), calcada acá.
 * Contrato CONTRATO-PUESTA-EN-MARCHA.md, «B», «D» y «E».
 *
 * El comercio es un CÍRCULO con su código grande y el nombre debajo, rodeado de un ANILLO con un arco por
 * ítem que se llena a medida que se crean. «Creación del comercio» a la izquierda, «Pasarela» a la
 * derecha; abajo, el sistema administrativo (a la izquierda, un libro mayor) y el kit de bienvenida (a
 * la derecha, una hoja). El ítem que se crea tira un trazo fino azul hasta el comercio con un punto
 * que viaja; al quedar hecho, una onda fina sale del anillo. Arriba al centro, el titular y su renglón.
 * Lo que falla queda en rojo en su lugar. Al final: si quedó operativa, un anillo fino que se abre, el
 * ✓ fino en el círculo y confeti en los dos colores del logo (~90 papelitos, ~3 s); si algo falló,
 * debajo de la escena y en HTML, sólo la lista de lo que falló con su «Reintentar».
 *
 * Juan: *«mantenlo simple… nada más que las líneas… azules»*. Un solo acento, el celeste de CrediTotal
 * (`--pem-acento`); el ámbar del logo sólo en el confeti; el rojo sólo en lo que falla. Sin sombras,
 * sin luces de fondo, sin degradados. Los colores son variables del :host, leídas con getComputedStyle.
 *
 * El motor no cambió: la consola sondea cada 500 ms y pasa el objeto nuevo por `puesta`; la escena anima
 * las TRANSICIONES una por vez, en el orden de sus `at` (más rápido si se atrasó); abrir una que ya
 * terminó muestra el cuadro final quieto, sin fiesta; respeta `prefers-reduced-motion`; se detiene
 * fuera de vista y con la pestaña oculta; y un `aria-live` dice lo mismo que el titular.
 */

type EstadoPaso = 'PENDIENTE' | 'EN_CURSO' | 'HECHO' | 'VERIFICADO' | 'FALLO' | 'NO_APLICA' | 'ESPERANDO';
/** 0: izquierda (Creación del comercio) · 1: derecha (Pasarela) · 2: abajo a la izquierda (Sistema
 *  administrativo) · 3: abajo a la derecha (Entrega, y cualquier grupo que no se conozca). */
type Lado = 0 | 1 | 2 | 3;

/** Lo que la escena lee de un paso. Compatible con `PasoPuestaEnMarcha` de `app-puesta-en-marcha`. */
export interface EscenaPaso {
  clave: string;
  nombre: string;
  estado: string;
  detalle?: string | null;
  error?: string | null;
  /** El PDF del kit de bienvenida (u otro archivo del paso). */
  enlace?: string | null;
  obligatorio?: boolean;
  at?: string | Date | null;
  /** Cuántas veces corrió este paso — más de 1 dice que YA se reintentó (para saber desde cuándo
   *  contar «en N min»: el `at` de un paso sin reintentar es del intento viejo). */
  intentos?: number;
  /** Si falló por un dato incompleto: qué completar (hoy, el domicilio fiscal). */
  completar?: { campo: 'fiscal'; nombre: string } | null;
}

export interface EscenaGrupo { clave: string; nombre: string; estado?: string; pasos: EscenaPaso[]; }

/** `solicitud.puesta_en_marcha` (`aPantalla`). */
export interface EscenaPuesta {
  estado: string;
  grupos?: EscenaGrupo[] | null;
  pasos?: EscenaPaso[] | null;
  /** EN_CURSO sin tocarse hace más de 2 minutos (se reinició el proceso): se trata como fallo que se reintenta. */
  cortada?: boolean;
  iniciada_at?: string | Date | null;
  at?: string | Date | null;
}

export interface EscenaComercio { nombre?: string | null; codigo?: string | null; }

/** Una fila de la lista de lo que falló, debajo de la escena. */
export interface FalloEscena { clave: string; nombre: string; grupo: string; porque: string; esperan: string[]; completar?: { campo: 'fiscal'; nombre: string } | null; }

interface Plano { paso: EscenaPaso; lado: Lado; grupo: string; grupoNombre: string; }

interface Item {
  clave: string;
  nombre: string;
  grupo: string;
  grupoNombre: string;
  lado: Lado;
  indice: number;
  orden: number;
  /** El último estado que llegó del servidor. */
  real: EstadoPaso;
  /** Lo que se está dibujando (va atrás del servidor, una transición por vez). HECHO y VERIFICADO se pintan igual. */
  visual: EstadoPaso;
  t0: number;          // cuándo empezó a dibujarse el trazo (EN_CURSO), en el reloj de la pantalla
  t1: number;          // cuándo quedó hecho o falló
  detalle: string | null;
  error: string | null;
  enlace: string | null;
  completar: { campo: 'fiscal'; nombre: string } | null;
  /** El núcleo lo dejó ESPERANDO (p. ej. a que Google active la dirección): se dibuja en curso, con su propio texto. */
  esperando?: boolean;
  /** Espera por algo que falló: «espera a la llave». */
  espera: string | null;
  at: number | null;
  x: number;
  y: number;
}

interface Evento { clave: string | null; a: EstadoPaso | 'CELEBRAR' | 'FINAL'; at: number | null; orden: number; rapido: boolean; }

interface Particula {
  t: 'ring' | 'conf';
  x: number; y: number; color: string; life: number;
  r0: number; r1: number; dur: number; w: number;
  vx: number; vy: number; rot: number; vr: number; s: number; max: number; ph: number;
}

interface Paleta { fg: string; mf: string; bd: string; card: string; mal: string; tenue: string; brillo: string; acento: string; ambar: string; alerta: string; }

interface Titular { t: string; sub: string; color: string; punto: boolean; chk: boolean; fin: boolean; }

const ESTADOS: EstadoPaso[] = ['PENDIENTE', 'EN_CURSO', 'HECHO', 'VERIFICADO', 'FALLO', 'NO_APLICA', 'ESPERANDO'];
const HECHO = (e: EstadoPaso) => e === 'HECHO' || e === 'VERIFICADO';
const CLAVES_PASARELA = /^(PASARELA|PROVEEDOR|DEPARTAMENTO|LLAVE|CREDENCIAL|VERIFICACION)/;
const CLAVES_ENTREGA = /^(KIT|ENTREGA|BIENVENIDA)/;
const CLAVES_ADMINISTRATIVO = /^(ALTA_ADMINISTRATIVO|ADMINISTRATIVO)/;

/** De qué depende cada clave: copia de `CLAVES` en `modules/workflow/dominio/puesta-en-marcha.js` (`*GRUPO` = todo ese grupo). */
const DEPENDE: Record<string, string[]> = {
  COMERCIO: [], CONTACTOS: ['COMERCIO'], CONDICIONES: ['COMERCIO'], DOCUMENTOS: ['COMERCIO'], FICHA: ['COMERCIO'],
  USUARIO_PORTAL: ['COMERCIO', 'CONTACTOS'], CUENTA_BANCARIA: ['COMERCIO'], TIENDA_CAJA: ['COMERCIO'],
  DIRECCION_PROPIA: ['COMERCIO'],
  ENTREGA_ACCESOS: ['USUARIO_PORTAL', 'DIRECCION_PROPIA'],
  USUARIO_GERENTE: ['TIENDA_CAJA', 'DIRECCION_PROPIA'], USUARIO_CAJA: ['TIENDA_CAJA', 'DIRECCION_PROPIA'],
  USUARIO_VENDEDOR: ['TIENDA_CAJA', 'DIRECCION_PROPIA'],
  PROVEEDOR: ['COMERCIO'], DEPARTAMENTOS: ['PROVEEDOR'], LLAVE: ['DEPARTAMENTOS'], CREDENCIAL: ['DEPARTAMENTOS'],
  VERIFICACION: ['PROVEEDOR', 'DEPARTAMENTOS', 'LLAVE', 'CREDENCIAL'], ALTA_ADMINISTRATIVO: ['COMERCIO'],
  KIT_BIENVENIDA: ['*COMERCIO', '*PASARELA', '*ADMINISTRATIVO'],
};

/** Las frases de cada clave del núcleo, con su concordancia: [haciendo, hecho, falló, corto (para «espera a…»)]. */
const FRASES: Record<string, [string, string, string, string]> = {
  COMERCIO: ['Creando el comercio', 'Comercio creado', 'No se pudo crear el comercio', 'el comercio'],
  CONTACTOS: ['Cargando los contactos', 'Contactos cargados', 'No se pudieron cargar los contactos', 'los contactos'],
  CONDICIONES: ['Creando las condiciones V1', 'Condiciones V1 aprobadas', 'No se pudieron crear las condiciones V1', 'las condiciones'],
  DOCUMENTOS: ['Pasando los documentos a la ficha', 'Documentos en la ficha', 'No se pudieron pasar los documentos', 'los documentos'],
  FICHA: ['Aprobando la ficha', 'Ficha aprobada', 'No se pudo aprobar la ficha', 'la ficha'],
  USUARIO_PORTAL: ['Creando el usuario del portal', 'Usuario del portal creado', 'No se pudo crear el usuario del portal', 'el usuario del portal'],
  DIRECCION_PROPIA: ['Preparando la dirección propia del portal', 'Dirección propia del portal lista',
    'No se pudo crear la dirección propia del portal', 'la dirección propia del portal'],
  ENTREGA_ACCESOS: ['Entregando los accesos', 'Accesos entregados', 'No se pudieron entregar los accesos', 'la entrega de accesos'],
  CUENTA_BANCARIA: ['Registrando la cuenta bancaria', 'Cuenta bancaria registrada', 'No se pudo registrar la cuenta bancaria', 'la cuenta bancaria'],
  TIENDA_CAJA: ['Creando la tienda y la caja', 'Tienda y caja creadas', 'No se pudieron crear la tienda y la caja', 'la tienda y la caja'],
  USUARIO_GERENTE: ['Creando el usuario del gerente', 'Usuario del gerente creado', 'No se pudo crear el usuario del gerente', 'el usuario del gerente'],
  USUARIO_CAJA: ['Creando el usuario de la caja', 'Usuario de la caja creado', 'No se pudo crear el usuario de la caja', 'el usuario de la caja'],
  USUARIO_VENDEDOR: ['Creando el usuario del vendedor', 'Usuario del vendedor creado', 'No se pudo crear el usuario del vendedor', 'el usuario del vendedor'],
  PROVEEDOR: ['Creando el proveedor en la pasarela', 'Proveedor creado', 'No se pudo crear el proveedor', 'el proveedor'],
  DEPARTAMENTOS: ['Creando los departamentos', 'Departamentos creados', 'No se pudieron crear los departamentos', 'los departamentos'],
  LLAVE: ['Creando la llave', 'Llave creada', 'No se pudo crear la llave', 'la llave'],
  CREDENCIAL: ['Creando la credencial', 'Credencial creada', 'No se pudo crear la credencial', 'la credencial'],
  VERIFICACION: ['Verificando la pasarela', 'Pasarela verificada', 'No se pudo verificar la pasarela', 'la verificación'],
  ALTA_ADMINISTRATIVO: ['Dando de alta en el sistema administrativo', 'Alta en el sistema administrativo',
    'No se pudo dar de alta en el sistema administrativo', 'el sistema administrativo'],
  KIT_BIENVENIDA: ['Armando el kit de bienvenida', 'Kit de bienvenida listo', 'No se pudo armar el kit de bienvenida', 'el kit'],
};

/** Los tiempos de la maqueta: DUR (el trazo hasta el comercio) y POS (cuánto se lee lo recién hecho). */
const MOTOR = { dur: 700, pos: 420, rapido: 160, anillo: 900 };

const SANS = 'Montserrat, Inter, system-ui, -apple-system, sans-serif';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const letra = (px: number, peso = 400, fam = SANS) => `${peso} ${px}px ${fam}`;

@Component({
  selector: 'app-puesta-en-marcha-escena',
  templateUrl: './puesta-en-marcha-escena.component.html',
  styleUrls: ['./puesta-en-marcha-escena.component.css'],
})
export class PuestaEnMarchaEscenaComponent implements OnChanges, OnDestroy {

  @Input() puesta: EscenaPuesta | null | undefined = null;
  @Input() comercio: EscenaComercio | null | undefined = null;
  /** Si quien mira puede reintentar (`acciones.reintentar_puesta_en_marcha`): sin eso, la lista no trae botones. */
  @Input() puedeReintentar = true;
  /** Mientras un reintento está en vuelo, los botones quedan apagados. */
  @Input() ocupado = false;
  /** Simula la secuencia completa (~600 ms por ítem), sondeando cada 500 ms como la consola. */
  @Input() demo = false;
  /** En el modo demo: falla la cuenta bancaria (lo demás sigue) o la llave (lo que depende espera). */
  @Input() demoFalla: 'cuenta' | 'llave' | null = null;

  /** El «Reintentar» de un ítem de la lista: la clave del paso. */
  @Output() reintentarPaso = new EventEmitter<string>();
  /** «Completar el domicilio»: el paso falló por un dato incompleto (clave del paso). */
  @Output() completarPaso = new EventEmitter<string>();
  /** «Reintentar todo»: más de un fallo, o la puesta en marcha se cortó. */
  @Output() reintentarTodo = new EventEmitter<void>();

  @ViewChild('caja') cajaRef?: ElementRef<HTMLDivElement>;
  @ViewChild('sondas') sondasRef?: ElementRef<HTMLDivElement>;
  @ViewChild('lienzo') set lienzoRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref && ref.nativeElement !== this.lienzo) this.montar(ref.nativeElement);
    if (!ref && this.lienzo) this.desmontar();
  }

  /** Lo que dice el `aria-live` (y el rótulo del lienzo): lo mismo que el titular. */
  anuncio = '';
  rotulo = '';
  /** Lo que falló, cuando la escena ya terminó de contarlo (la lista HTML de abajo). */
  fallos: FalloEscena[] = [];
  /** El kit para descargar, cuando quedó OPERATIVA. */
  enlaceKit: string | null = null;
  resumenFallos = '';

  private lienzo: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private W = 0;
  private H = 440;
  private items: Item[] = [];
  private porClave = new Map<string, Item>();
  private gruposNombre: [string, string, string, string] = ['Creación del comercio', 'Pasarela', 'Sistema administrativo', 'Entrega'];
  private estadoReal = '';
  private cortada = false;
  private primera = true;
  private cola: Evento[] = [];
  private libreDesde = 0;
  private actual: Item | null = null;
  private actualHasta = 0;
  /** corriendo · operativa · fallo: lo que la escena ya terminó de contar (va atrás del servidor). */
  private fase: 'corriendo' | 'operativa' | 'fallo' = 'corriendo';
  private tFin = 0;
  private fiestaHecha = true;
  private parts: Particula[] = [];
  private ultimoCuadro = 0;
  private inicioServidor: number | null = null;
  private finServidor: number | null = null;
  private pal: Paleta = {
    fg: 'CanvasText', mf: 'GrayText', bd: 'GrayText', card: 'Canvas', mal: 'CanvasText', tenue: 'GrayText',
    brillo: 'Canvas', acento: 'CanvasText', ambar: 'CanvasText', alerta: 'CanvasText',
  };
  private reducir = false;
  private visible = true;
  private raf = 0;
  private enCuadro = false;
  private ro: ResizeObserver | null = null;
  private io: IntersectionObserver | null = null;
  private mo: MutationObserver | null = null;
  private mq: MediaQueryList | null = null;
  private geo = { cx: 0, cy: 205, R: 52, xl: 0, xr: 0 };
  private demoTimers: number[] = [];
  private demoServidor: EscenaPuesta | null = null;
  private demoReintentar: ((clave: string) => void) | null = null;

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  get hayPasos(): boolean { return this.items.length > 0; }

  ngOnChanges(cambios: SimpleChanges): void {
    if (cambios['comercio'] && this.ctx) this.pedirCuadro();
    if (this.demo) {
      if (!this.demoServidor || cambios['demoFalla']) { this.pararDemo(); this.reiniciarEscena(); this.arrancarDemo(); }
      return;
    }
    if (this.demoServidor) { this.pararDemo(); this.reiniciarEscena(); }
    if (cambios['puesta'] || this.primera) this.aplicar(this.puesta || null);
  }

  ngOnDestroy(): void { this.pararDemo(); this.desmontar(); }

  pedirReintentoPaso(clave: string): void {
    if (this.demo && this.demoReintentar) { this.demoReintentar(clave); return; }
    this.reintentarPaso.emit(clave);
  }

  pedirCompletar(clave: string): void { this.completarPaso.emit(clave); }

  pedirReintentoTodo(): void {
    if (this.demo && this.demoReintentar) { const r = this.demoReintentar; this.fallos.forEach((f) => r(f.clave)); return; }
    this.reintentarTodo.emit();
  }

  // ─── lo que llega del servidor ────────────────────────────────────────────

  /**
   * Se pinta lo que venga en `grupos[]`: COMERCIO a la izquierda, PASARELA a la derecha, ADMINISTRATIVO
   * abajo a la izquierda y ENTREGA (o cualquier otro) abajo a la derecha. Sin esas claves: el primero a
   * la izquierda, el segundo a la derecha y el resto abajo a la derecha. La forma vieja (§B, `pasos`
   * sueltos) se reparte por la clave del paso.
   */
  private aplanar(p: EscenaPuesta): Plano[] {
    const nombres: [string, string, string, string] = ['', '', '', ''];
    const poner = (l: Lado, n: string) => { if (n && !nombres[l].split(' · ').includes(n)) nombres[l] = nombres[l] ? `${nombres[l]} · ${n}` : n; };
    let planos: Plano[];
    if (p.grupos && p.grupos.length) {
      const hay = (c: string) => (p.grupos || []).some((g) => g.clave === c);
      const conClaves = hay('COMERCIO') || hay('PASARELA');
      const ladoDe = (g: EscenaGrupo, i: number): Lado => {
        if (conClaves) return g.clave === 'COMERCIO' ? 0 : g.clave === 'PASARELA' ? 1 : g.clave === 'ADMINISTRATIVO' ? 2 : 3;
        return i === 0 ? 0 : i === 1 ? 1 : 3;
      };
      planos = p.grupos.flatMap((g, i) => (g.pasos || []).map((paso) => ({ paso, lado: ladoDe(g, i), grupo: g.clave, grupoNombre: g.nombre || g.clave || '' })));
    } else {
      planos = (p.pasos || []).map((paso) => {
        const lado: Lado = CLAVES_ADMINISTRATIVO.test(paso.clave) ? 2 : CLAVES_ENTREGA.test(paso.clave) ? 3 : CLAVES_PASARELA.test(paso.clave) ? 1 : 0;
        const grupo = ['COMERCIO', 'PASARELA', 'ADMINISTRATIVO', 'ENTREGA'][lado];
        const grupoNombre = ['Creación del comercio', 'Pasarela', 'Sistema administrativo', 'Entrega'][lado];
        return { paso, lado, grupo, grupoNombre };
      });
    }
    planos.forEach((x) => poner(x.lado, x.grupoNombre));
    this.gruposNombre = nombres;
    return planos;
  }

  private aplicar(p: EscenaPuesta | null): void {
    const pasos = p ? this.aplanar(p) : [];
    if (!pasos.length) { this.reiniciarEscena(); return; }
    const ahora = performance.now();
    if (pasos.map((x) => x.paso.clave).join('|') !== this.items.map((i) => i.clave).join('|')) this.rearmar(pasos);
    this.cortada = !!p?.cortada;
    // Cuándo arrancó ESTA corrida, para «en N min»: mezclar el `at` de TODOS los pasos adelanta el
    // inicio al primer intento en cuanto hay un reintento — el `at` de lo que no se volvió a tocar
    // sigue siendo del intento viejo (medido 29-sep: «14 de 14 · en 21 min 37 s» que venía de la
    // primera corrida, no del último reintento). Orden: `iniciada_at` si el núcleo la manda (exacta);
    // si no, y hubo reintento, el más viejo de LOS PASOS RETENTADOS (`intentos` > 1, que son los de
    // esta corrida); si no hubo reintento, el más viejo de todos (el único intento que hay). Sin
    // ninguna forma segura, se deja sin duración (`inicioServidor` en null).
    const iniciadaAt = p?.iniciada_at ? new Date(p.iniciada_at).getTime() : NaN;
    const huboReintento = pasos.some(({ paso }) => (paso.intentos || 0) > 1);
    if (!isNaN(iniciadaAt)) {
      this.inicioServidor = iniciadaAt;
    } else if (huboReintento) {
      const deEsteIntento = pasos
        .filter(({ paso }) => (paso.intentos || 0) > 1)
        .map(({ paso }) => (paso.at ? new Date(paso.at).getTime() : NaN))
        .filter((n) => !isNaN(n));
      this.inicioServidor = deEsteIntento.length ? Math.min(...deEsteIntento) : null;
    } else {
      const todos = pasos.map(({ paso }) => (paso.at ? new Date(paso.at).getTime() : NaN)).filter((n) => !isNaN(n));
      if (todos.length) this.inicioServidor = Math.min(...todos);
    }

    const eventos: Evento[] = [];
    pasos.forEach(({ paso }, orden) => {
      const it = this.porClave.get(paso.clave);
      if (!it) return;
      let estado = (ESTADOS.includes(paso.estado as EstadoPaso) ? paso.estado : 'PENDIENTE') as EstadoPaso;
      let error = paso.error || null;
      // Cortada: lo que quedó EN_CURSO no lo termina nadie; se pinta como un fallo que se reintenta.
      if (this.cortada && estado === 'EN_CURSO') { estado = 'FALLO'; error = error || 'Se cortó a mitad de camino: el proceso se reinició. Reinténtalo.'; }
      const at = paso.at ? new Date(paso.at).getTime() : NaN;
      if (!isNaN(at)) this.finServidor = this.finServidor === null ? at : Math.max(this.finServidor, at);
      it.nombre = paso.nombre || it.nombre;
      it.detalle = paso.detalle || null;
      it.esperando = estado === 'ESPERANDO';
      it.error = estado === 'FALLO' ? (error || paso.detalle || 'No se pudo.') : null;
      it.enlace = paso.enlace || null;
      it.completar = estado === 'FALLO' ? (paso.completar || null) : null;
      it.orden = orden;
      if (estado === it.real) return;
      const antes = it.real;
      it.real = estado;
      it.at = isNaN(at) ? null : at;
      // Si el núcleo fue más rápido que el sondeo y el paso llegó hecho sin haberse visto en curso, igual
      // se cuenta su trazo: primero en curso, después hecho.
      if (HECHO(estado) && antes !== 'EN_CURSO' && antes !== 'ESPERANDO' && !HECHO(antes)) eventos.push({ clave: it.clave, a: 'EN_CURSO', at: it.at, orden, rapido: false });
      // ESPERANDO se dibuja como en curso (el texto propio sale de `esperando`).
      eventos.push({ clave: it.clave, a: estado === 'ESPERANDO' ? 'EN_CURSO' : estado, at: it.at, orden, rapido: false });
    });

    const general = this.cortada ? 'FALLO' : String(p?.estado || '');
    if (this.primera) {
      this.primera = false;
      const enVivo = general === 'EN_CURSO' && !this.reducir;
      if (!enVivo) {
        // Ya terminada al abrirla (o menos movimiento): el cuadro final quieto, sin fiesta.
        this.items.forEach((it) => { it.visual = it.real; it.t0 = ahora - 1e6; it.t1 = ahora - 1e6; });
        eventos.length = 0;
        this.actual = null;
        if (general === 'OPERATIVA') { this.fase = 'operativa'; this.tFin = ahora - 1e6; this.fiestaHecha = true; }
        if (general === 'FALLO') this.fase = 'fallo';
        this.marcarEsperas();
        this.actualizarLista();
      } else {
        // Corriendo: lo que ya estaba hecho se repasa rápido, en orden, sin ondas.
        for (let i = eventos.length - 1; i >= 0; i--) if (eventos[i].a === 'EN_CURSO' && eventos[i + 1]?.clave === eventos[i].clave) eventos.splice(i, 1);
        eventos.forEach((e) => { e.rapido = true; });
        this.items.filter((i) => i.real === 'NO_APLICA').forEach((i) => { i.visual = 'NO_APLICA'; });
      }
    }
    eventos.sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity) || a.orden - b.orden || (a.a === 'EN_CURSO' ? -1 : 1));
    this.cola.push(...eventos.filter((e) => !(e.a === 'NO_APLICA' && this.porClave.get(e.clave || '')?.visual === 'NO_APLICA')));
    if (general !== this.estadoReal && general === 'OPERATIVA') this.cola.push({ clave: null, a: 'CELEBRAR', at: null, orden: 9999, rapido: false });
    if (general !== this.estadoReal && general === 'FALLO' && this.fase !== 'fallo') this.cola.push({ clave: null, a: 'FINAL', at: null, orden: 9999, rapido: false });
    if (general === 'EN_CURSO' && this.fase !== 'corriendo') { this.fase = 'corriendo'; this.actualizarLista(); }
    this.estadoReal = general;
    this.anunciar(ahora);
    this.pedirCuadro();
  }

  private rearmar(pasos: Plano[]): void {
    const antes = this.porClave;
    this.porClave = new Map();
    const indices = [0, 0, 0, 0];
    this.items = pasos.map(({ paso, lado, grupo, grupoNombre }, orden) => {
      const it: Item = antes.get(paso.clave) || {
        clave: paso.clave, nombre: paso.nombre, grupo, grupoNombre, lado, indice: 0, orden,
        real: 'PENDIENTE', visual: 'PENDIENTE', t0: -1e9, t1: -1e9,
        detalle: null, error: null, enlace: null, completar: null, espera: null, at: null, x: 0, y: 0,
      };
      it.lado = lado; it.grupo = grupo; it.grupoNombre = grupoNombre; it.orden = orden;
      it.indice = indices[lado]++;
      this.porClave.set(it.clave, it);
      return it;
    });
    this.cola = this.cola.filter((e) => !e.clave || this.porClave.has(e.clave));
    if (this.actual && !this.porClave.has(this.actual.clave)) this.actual = null;
    this.medir();
  }

  // ─── el motor: una transición por vez (el `paso` de la maqueta, alimentado por el servidor) ────

  private procesar(ahora: number): void {
    while (this.cola.length && ahora >= this.libreDesde) {
      const ev = this.cola.shift() as Evento;
      const atraso = ev.rapido ? 1 : this.cola.length >= 3 ? 0.45 : 1;
      const sosten = this.aplicarEvento(ev, ahora, atraso);
      this.libreDesde = ahora + (this.reducir ? 0 : sosten);
      this.actualHasta = this.libreDesde;
      this.anunciar(ahora);
    }
  }

  private aplicarEvento(ev: Evento, ahora: number, atraso: number): number {
    const { cx, cy, R } = this.geo;
    const azul = this.pal.acento;
    if (ev.a === 'FINAL') {
      // Terminó con algo en rojo: arriba dice cuánto falta y debajo de la escena sale la lista.
      this.actual = null; this.fase = 'fallo'; this.tFin = ahora;
      this.marcarEsperas(); this.actualizarLista();
      return 0;
    }
    if (ev.a === 'CELEBRAR') {
      this.actual = null; this.fase = 'operativa'; this.tFin = ahora;
      this.fiestaHecha = this.reducir;
      this.actualizarLista();
      return 0;
    }
    const it = this.porClave.get(ev.clave || '');
    if (!it) return 0;
    this.actual = it;
    switch (ev.a) {
      case 'EN_CURSO':
        it.visual = 'EN_CURSO'; it.t0 = this.reducir ? ahora - 1e6 : ahora; it.espera = null;
        return MOTOR.dur * atraso;
      case 'HECHO':
      case 'VERIFICADO': {
        const ya = HECHO(it.visual);
        it.visual = ev.a; it.t1 = ahora; it.espera = null;
        if (it.t0 < ahora - 1e5) it.t0 = ahora - 1e6;
        this.marcarEsperas();
        if (ya) return 0;
        if (!ev.rapido) {
          // `recien` de la maqueta: una onda fina sale del anillo (y de su ícono, si es de abajo).
          if (it.lado >= 2) this.onda(it.x, it.y, azul, 10, 50, 800);
          if (it.lado !== 3) this.onda(cx, cy, azul, R + 10, R + 40, 700, 1.5);
        }
        return ev.rapido ? MOTOR.rapido : MOTOR.pos * atraso;
      }
      case 'FALLO':
        if (it.visual !== 'EN_CURSO') it.t0 = ahora - 1e6;
        it.visual = 'FALLO'; it.t1 = ahora;
        this.marcarEsperas();
        return ev.rapido ? MOTOR.rapido : MOTOR.pos * 2 * atraso;
      case 'NO_APLICA':
        it.visual = 'NO_APLICA';
        return 0;
      default: // PENDIENTE: se reintenta
        it.visual = 'PENDIENTE'; it.t0 = -1e9; it.t1 = -1e9;
        this.marcarEsperas();
        return 0;
    }
  }

  private frases(it: Item): [string, string, string, string] {
    const fija = FRASES[it.clave];
    if (fija) return fija;
    const n = (it.nombre || it.clave).trim();
    const min = n.charAt(0).toLowerCase() + n.slice(1);
    return [`Creando ${min}`, n, `No se pudo: ${n}`, min];
  }

  /** Lo que espera por algo que falló: su punto queda con borde rojo punteado y dice «espera a…». */
  private marcarEsperas(): void {
    this.items.forEach((i) => { i.espera = null; });
    this.items.filter((f) => f.visual === 'FALLO').forEach((f) => {
      const corto = this.frases(f)[3];
      this.dependientesDe(f.clave).forEach((d) => {
        const x = this.porClave.get(d);
        if (x && x.visual === 'PENDIENTE' && !x.espera) x.espera = corto;
      });
    });
  }

  /** Los que dependen (directa o indirectamente) de una clave. Una clave que la escena no conoce se
   *  reconoce por su `detalle` del núcleo («Espera a «Llave de API».»). */
  private dependientesDe(clave: string): string[] {
    const necesita = (c: string): string[] => (DEPENDE[c] || [])
      .flatMap((x) => (x.startsWith('*') ? this.items.filter((i) => i.grupo === x.slice(1)).map((i) => i.clave) : [x]));
    const fuera = new Set([clave]);
    let crecio = true;
    while (crecio) {
      crecio = false;
      for (const i of this.items) {
        if (fuera.has(i.clave)) continue;
        const porTexto = !DEPENDE[i.clave] && !!i.detalle && [...fuera].some((f) => { const n = this.porClave.get(f)?.nombre; return !!n && !!i.detalle?.includes(n); });
        if (necesita(i.clave).some((x) => fuera.has(x)) || porTexto) { fuera.add(i.clave); crecio = true; }
      }
    }
    fuera.delete(clave);
    return this.items.filter((i) => fuera.has(i.clave) && i.visual !== 'NO_APLICA').map((i) => i.clave);
  }

  /** La lista de abajo (sólo cuando la escena ya contó el final) y el enlace del kit. */
  private actualizarLista(): void {
    const fallos: FalloEscena[] = this.fase !== 'fallo' ? [] : this.items.filter((i) => i.visual === 'FALLO').map((f) => ({
      clave: f.clave, nombre: f.nombre, grupo: f.grupoNombre, porque: f.error || 'No se pudo.', completar: f.completar,
      esperan: this.dependientesDe(f.clave).map((d) => this.porClave.get(d)).filter((x): x is Item => !!x && x.visual === 'PENDIENTE').map((x) => x.nombre),
    }));
    const kit = this.items.find((i) => i.clave === 'KIT_BIENVENIDA' && HECHO(i.visual) && !!i.enlace);
    // Con el almacenamiento local el núcleo guarda la ruta sola (`/uploads/…`): se sirve desde el núcleo, no desde la
    // consola. Mismo criterio que `urlDocumento` del servicio de altas.
    // ALTA-33: el kit no espera a la dirección propia (`excepto` en el núcleo): con su paso hecho se ofrece, aunque la
    // puesta siga esperando a Google y la escena no haya llegado a «operativa».
    const crudo = kit ? kit.enlace : null;
    const enlace = crudo && crudo.startsWith('/') ? `${environment.BASE_LA}${crudo}` : crudo;
    const c = this.cuenta();
    const resumen = `Lo demás quedó creado (${c.listos} de ${c.total}). Reinténtalo cuando quieras.`;
    if (JSON.stringify([fallos, enlace, resumen]) === JSON.stringify([this.fallos, this.enlaceKit, this.resumenFallos])) return;
    this.fallos = fallos; this.enlaceKit = enlace; this.resumenFallos = resumen;
    if (this.enCuadro) this.zone.run(() => this.cdr.detectChanges());
  }

  private cuenta(lado?: Lado): { listos: number; total: number } {
    const xs = this.items.filter((i) => i.visual !== 'NO_APLICA' && (lado === undefined || i.lado === lado));
    return { listos: xs.filter((i) => HECHO(i.visual)).length, total: xs.length };
  }

  private textoCuenta(lado?: Lado): string { const k = this.cuenta(lado); return `${k.listos} de ${k.total}`; }

  private nombreComercio(): string { return (this.comercio?.nombre || '').trim() || 'El comercio'; }

  /** Qué está pasando, arriba al centro (el `estadoArriba` de la maqueta). */
  private titular(ahora: number): Titular {
    const C = this.pal;
    const a = this.actual;
    if (a && (a.visual === 'EN_CURSO' || ahora < this.actualHasta)) {
      const [haciendo, hecho, fallo] = this.frases(a);
      if (a.visual === 'EN_CURSO' && a.esperando) return { t: a.clave === 'DIRECCION_PROPIA' ? 'Esperando a que Google active la dirección' : `Esperando: ${haciendo.toLowerCase()}`, sub: a.detalle || '', color: C.alerta, punto: true, chk: false, fin: false };
      if (a.visual === 'EN_CURSO') return { t: `${haciendo}…`, sub: a.detalle || `${a.grupoNombre} · ${this.textoCuenta(a.lado)}`, color: C.fg, punto: true, chk: false, fin: false };
      if (HECHO(a.visual)) return { t: hecho, sub: a.detalle || `${a.grupoNombre} · ${this.textoCuenta(a.lado)}`, color: C.fg, punto: false, chk: true, fin: false };
      if (a.visual === 'FALLO') return { t: fallo, sub: a.error || '', color: C.mal, punto: false, chk: false, fin: false };
    }
    if (this.fase === 'operativa') {
      const dur = this.inicioServidor !== null && this.finServidor !== null && this.finServidor >= this.inicioServidor
        ? duracion(this.finServidor - this.inicioServidor) : '';
      return { t: `¡${this.nombreComercio()} ya puede operar!`, sub: `${this.textoCuenta()} listos${dur ? ` · en ${dur}` : ''}`, color: C.fg, punto: false, chk: false, fin: true };
    }
    if (this.fase === 'fallo') {
      const n = this.items.filter((i) => i.visual === 'FALLO').length || 1;
      return {
        t: n === 1 ? 'Queda 1 ítem por resolver' : `Quedan ${n} ítems por resolver`,
        sub: 'Lo demás ya quedó creado. Abajo está lo que falló.', color: C.fg, punto: false, chk: false, fin: false,
      };
    }
    const desde = this.inicioServidor !== null ? horaCorta(this.inicioServidor) : '';
    return { t: `Poniendo en marcha a ${this.nombreComercio()}`, sub: desde ? `desde las ${desde}` : `${this.textoCuenta()} listos`, color: C.mf, punto: false, chk: false, fin: false };
  }

  private anunciar(ahora: number): void {
    const e = this.titular(ahora);
    const nuevo = [e.t, e.sub].filter(Boolean).join(' · ');
    const cod = this.comercio?.codigo ? ` (${this.comercio.codigo})` : '';
    const rotulo = `Puesta en marcha de ${this.nombreComercio()}${cod}: ${this.textoCuenta()} listos. ${e.t}`;
    if (nuevo === this.anuncio && rotulo === this.rotulo) return;
    this.anuncio = nuevo; this.rotulo = rotulo;
    if (this.enCuadro) this.zone.run(() => this.cdr.detectChanges());
  }

  // ─── el lienzo ────────────────────────────────────────────────────────────

  private montar(c: HTMLCanvasElement): void {
    this.lienzo = c;
    this.ctx = c.getContext('2d');
    this.mq = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.reducir = !!this.mq?.matches;
    this.mq?.addEventListener?.('change', this.alCambiarMovimiento);
    this.leerTema();
    this.zone.runOutsideAngular(() => {
      const caja = this.cajaRef?.nativeElement || c.parentElement;
      if (typeof ResizeObserver !== 'undefined' && caja) {
        this.ro = new ResizeObserver(() => { this.medir(); this.pedirCuadro(); });
        this.ro.observe(caja);
      }
      if (typeof IntersectionObserver !== 'undefined') {
        this.io = new IntersectionObserver((es) => { this.visible = es.some((e) => e.isIntersecting); this.pedirCuadro(); });
        this.io.observe(c);
      }
      this.mo = new MutationObserver(() => { this.leerTema(); this.pedirCuadro(); });
      this.mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style', 'data-theme'] });
      document.addEventListener('visibilitychange', this.alCambiarPestana);
    });
    this.medir();
    this.pedirCuadro();
  }

  private desmontar(): void {
    cancelAnimationFrame(this.raf); this.raf = 0;
    this.ro?.disconnect(); this.io?.disconnect(); this.mo?.disconnect();
    this.ro = null; this.io = null; this.mo = null;
    this.mq?.removeEventListener?.('change', this.alCambiarMovimiento);
    document.removeEventListener('visibilitychange', this.alCambiarPestana);
    this.lienzo = null; this.ctx = null;
  }

  private alCambiarPestana = () => this.pedirCuadro();
  private alCambiarMovimiento = () => { this.reducir = !!this.mq?.matches; this.pedirCuadro(); };

  /** Los colores: las clases del tema y las variables `--pem-*` del :host, leídas de unas sondas. */
  private leerTema(): void {
    const s = this.sondasRef?.nativeElement || (this.lienzo?.parentElement?.querySelector('.sondas') as HTMLElement | null);
    if (!s) return;
    const leer = (sel: string, prop: 'color' | 'backgroundColor' | 'borderTopColor', prev: string) => {
      const el = s.querySelector(sel) as HTMLElement | null;
      const v = el ? getComputedStyle(el)[prop] : '';
      return v && !/rgba\(.*,\s*0\)$/.test(v) && v !== 'transparent' ? v : prev;
    };
    const p = this.pal;
    this.pal = {
      fg: leer('.s-frente', 'color', p.fg), mf: leer('.s-tenue', 'color', p.mf), bd: leer('.s-borde', 'borderTopColor', p.bd),
      card: leer('.s-fondo', 'backgroundColor', p.card), mal: leer('.s-peligro', 'color', p.mal), tenue: leer('.s-apagado', 'color', p.tenue),
      brillo: leer('.s-brillo', 'color', p.brillo), acento: leer('.s-exito', 'color', p.acento), ambar: leer('.s-ambar', 'color', p.ambar),
      alerta: leer('.s-alerta', 'color', p.alerta),
    };
  }

  /** La geometría de la maqueta: el comercio en (W/2, 205) con R = 52, las columnas a los lados y lo de abajo en cy + R + 118. */
  private medir(): void {
    const c = this.lienzo;
    const ctx = this.ctx;
    if (!c || !ctx) return;
    const caja = this.cajaRef?.nativeElement || c.parentElement;
    const W = Math.max(300, Math.floor(caja?.clientWidth || c.clientWidth || 700));
    const H = 440;
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    this.W = W; this.H = H;
    c.style.height = `${H}px`;
    c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cx = W / 2;
    const cy = 205;
    const R = 52;
    const xl = cx - Math.min(280, W * 0.26);
    const xr = cx + Math.min(250, W * 0.24);
    this.geo = { cx, cy, R, xl, xr };
    // Las columnas: de 31 en 31, centradas en la altura del comercio (la maqueta: 100…317 y 116…302).
    const columna = (lado: Lado, x: number) => {
      const xs = this.items.filter((i) => i.lado === lado);
      const paso = xs.length > 1 ? Math.min(31, 250 / (xs.length - 1)) : 0;
      const y0 = 209 - ((xs.length - 1) * paso) / 2;
      xs.forEach((it, k) => { it.x = x; it.y = y0 + k * paso; });
    };
    columna(0, xl); columna(1, xr);
    const abajo = (lado: Lado, x: number) => this.items.filter((i) => i.lado === lado).forEach((it, k) => { it.x = x + k * 190; it.y = cy + R + 118; });
    abajo(2, cx - Math.min(250, W * 0.23));
    abajo(3, cx + Math.min(60, W * 0.06));
  }

  /** El camino del trazo (el `bez` de la maqueta): del ítem al borde del anillo, o de la base del comercio al ícono de abajo. */
  private bez(it: Item, t: number): [number, number] {
    const { cx, cy, R } = this.geo;
    const u = 1 - t;
    if (it.lado >= 2) {
      const x0 = cx;
      const y0 = cy + R + 14;
      const x3 = it.x;
      const y3 = it.y - 18;
      const ym = (y0 + y3) / 2;
      return [u * u * u * x0 + 3 * u * u * t * x0 + 3 * u * t * t * x3 + t * t * t * x3, u * u * u * y0 + 3 * u * u * t * ym + 3 * u * t * t * ym + t * t * t * y3];
    }
    const ex = it.lado === 0 ? cx - R - 14 : cx + R + 14;
    const mx = (it.x + ex) / 2;
    return [u * u * u * it.x + 3 * u * u * t * mx + 3 * u * t * t * mx + t * t * t * ex, u * u * u * it.y + 3 * u * u * t * it.y + 3 * u * t * t * cy + t * t * t * cy];
  }

  private pedirCuadro(): void {
    if (this.raf || !this.ctx) return;
    this.zone.runOutsideAngular(() => { this.raf = requestAnimationFrame(this.cuadro); });
  }

  private cuadro = (ahora: number) => {
    this.raf = 0;
    if (!this.ctx) return;
    const dt = this.ultimoCuadro ? Math.min(50, ahora - this.ultimoCuadro) : 16.7;
    this.ultimoCuadro = ahora;
    this.enCuadro = true;
    try {
      this.procesar(ahora);
      this.dibujar(ahora, dt);
    } finally { this.enCuadro = false; }
    if (this.animando(ahora) && this.visible && !document.hidden) this.pedirCuadro();
    else this.ultimoCuadro = 0;
  };

  private animando(ahora: number): boolean {
    if (this.cola.length) return true;
    if (this.reducir) return false;
    if (ahora < this.libreDesde + 50) return true;
    if (this.parts.length) return true;
    if (this.items.some((i) => i.visual === 'EN_CURSO' && ahora - i.t0 < MOTOR.dur + 50)) return true;
    return this.fase === 'operativa' && ahora - this.tFin < 600; // el ✓ que se dibuja
  }

  // ─── el dibujo: «A · Constelación» en modo simple ─────────────────────────

  private dibujar(ahora: number, dt: number): void {
    const c = this.ctx as CanvasRenderingContext2D;
    const { W, H } = this;
    const C = this.pal;
    const azul = C.acento;
    const { cx, cy, R, xl, xr } = this.geo;
    c.clearRect(0, 0, W, H);
    if (!this.items.length) return;
    const fin = this.fase === 'operativa';
    const izq = this.items.filter((i) => i.lado === 0);
    const der = this.items.filter((i) => i.lado === 1);
    const adm = this.items.filter((i) => i.lado === 2);
    const kit = this.items.filter((i) => i.lado === 3);

    // los trazos
    const trazo = (it: Item, t0: number, t1: number, color: string, w: number, al: number, dash: number[] | null) => {
      c.save(); c.globalAlpha = al; c.strokeStyle = color; c.lineWidth = w;
      if (dash) c.setLineDash(dash);
      c.beginPath();
      for (let i = 0; i <= 44; i++) { const [px, py] = this.bez(it, t0 + ((t1 - t0) * i) / 44); if (i) c.lineTo(px, py); else c.moveTo(px, py); }
      c.stroke(); c.restore();
    };
    for (const it of this.items) {
      if (HECHO(it.visual)) trazo(it, 0, 1, azul, 1.1, 0.6, null);
      else if (it.visual === 'FALLO') trazo(it, 0, 0.5, C.mal, 1.2, 0.85, [4, 4]);
      else if (it.visual === 'EN_CURSO') {
        const e = this.reducir ? 1 : suave(Math.min(1, (ahora - it.t0) / MOTOR.dur));
        const tono = it.esperando ? C.alerta : azul;   // lo que se está generando (ESPERANDO) va en ámbar
        trazo(it, 0, e, tono, 1.3, 0.95, it.esperando ? [5, 3] : null);
        const [hx, hy] = this.bez(it, e);
        punto(c, hx, hy, 3.8, tono); punto(c, hx, hy, 1.6, C.brillo);
      }
    }

    // el comercio: un anillo con un arco por ítem, que se llena a medida que se crean
    const ap = this.items.filter((i) => i.visual !== 'NO_APLICA');
    const n = Math.max(1, ap.length);
    const gap = 0.07;
    const len = (2 * Math.PI - n * gap) / n;
    ap.forEach((it, i) => {
      const a0 = -Math.PI / 2 + i * (len + gap) + gap / 2;
      const a1 = a0 + len;
      c.lineWidth = 3.5; c.strokeStyle = C.bd; c.beginPath(); c.arc(cx, cy, R + 10, a0, a1); c.stroke();
      if (HECHO(it.visual) || it.visual === 'EN_CURSO') {
        const p = HECHO(it.visual) || this.reducir ? 1 : Math.min(1, (ahora - it.t0) / MOTOR.dur);
        c.strokeStyle = azul; c.beginPath(); c.arc(cx, cy, R + 10, a0, a0 + len * p); c.stroke();
      }
      if (it.visual === 'FALLO') { c.strokeStyle = C.mal; c.beginPath(); c.arc(cx, cy, R + 10, a0, a1); c.stroke(); }
    });
    c.fillStyle = C.card; c.beginPath(); c.arc(cx, cy, R, 0, 7); c.fill();
    c.strokeStyle = fin ? azul : C.bd; c.lineWidth = 1; c.stroke();
    c.textAlign = 'center'; c.textBaseline = 'alphabetic';
    const nombre = this.nombreComercio();
    if (fin) {
      const p = this.reducir ? 1 : Math.min(1, (ahora - this.tFin) / 500);
      c.save(); c.setLineDash([60 * p, 60]); check(c, cx - 12, cy - 4, 24, azul, 2); c.restore();
      c.font = letra(11.5, 600); c.fillStyle = C.fg; c.fillText(recortar(c, nombre, 2 * R - 12), cx, cy + 30);
    } else {
      const cod = (this.comercio?.codigo || '').trim();
      c.font = letra(24, 700, MONO); c.fillStyle = C.fg;
      if (cod) c.fillText(recortar(c, cod, 2 * R - 16), cx, cy + 2);
      c.font = letra(11.5, 600); c.fillStyle = C.mf; c.fillText(recortar(c, nombre, 2 * R - 12), cx, cod ? cy + 22 : cy + 4);
    }

    // los rótulos de los grupos de los costados, con su punto azul
    if (izq.length) { this.rotuloGrupo(c, `${this.gruposNombre[0]} · ${this.textoCuenta(0)}`, xl - 14, izq[0].y - 26, 'right', xl - 14 - 6); punto(c, xl, izq[0].y - 30, 4, azul); }
    if (der.length) { this.rotuloGrupo(c, `${this.gruposNombre[1]} · ${this.textoCuenta(1)}`, xr + 14, der[0].y - 26, 'left', W - 6 - (xr + 14)); punto(c, xr, der[0].y - 30, 4, azul); }
    for (const it of [...izq, ...der]) this.dibujarItem(c, it);

    // abajo: el sistema administrativo (libro mayor) y el kit (hoja)
    for (const it of adm) this.dibujarAbajo(c, it, ahora, 'libro');
    for (const it of kit) this.dibujarAbajo(c, it, ahora, 'hoja');

    // arriba: qué está pasando
    this.dibujarTitular(c, cx, 40, this.titular(ahora));

    // el final, una sola vez y sólo si se vio llegar: el anillo fino que se abre y el confeti de dos colores
    if (fin && !this.fiestaHecha) { this.fiestaHecha = true; this.fiesta(cx, cy); }
    this.particulas(c, dt);
  }

  private dibujarItem(c: CanvasRenderingContext2D, it: Item): void {
    const C = this.pal;
    const azul = C.acento;
    const { x: px, y: py } = it;
    const izq = it.lado === 0;
    const hecho = HECHO(it.visual);
    if (hecho) punto(c, px, py, 4.2, azul);
    else if (it.visual === 'EN_CURSO' && it.esperando) {
      // Reloj fino en ámbar: se distingue también sin color (accesibilidad).
      c.save(); c.strokeStyle = C.alerta; c.lineWidth = 1.4; c.fillStyle = C.card;
      c.beginPath(); c.arc(px, py, 5.6, 0, 7); c.fill(); c.stroke();
      c.beginPath(); c.moveTo(px, py - 3); c.lineTo(px, py); c.lineTo(px + 2.4, py + 1.4); c.stroke(); c.restore();
    }
    else if (it.visual === 'EN_CURSO') punto(c, px, py, 5.2, azul);
    else if (it.visual === 'FALLO') {
      punto(c, px, py, 5.5, C.mal);
      c.fillStyle = C.brillo; c.font = letra(8.5, 700); c.textAlign = 'center'; c.textBaseline = 'alphabetic'; c.fillText('!', px, py + 3);
    } else if (it.visual === 'NO_APLICA') punto(c, px, py, 1.8, C.tenue);
    else {
      c.save(); c.strokeStyle = it.espera ? C.mal : C.tenue; if (it.espera) c.setLineDash([2, 2]);
      c.lineWidth = 1; c.fillStyle = C.card; c.beginPath(); c.arc(px, py, 3.8, 0, 7); c.fill(); c.stroke(); c.restore();
    }
    const lx = izq ? px - 15 : px + 15;
    const chk = hecho ? 17 : 0;
    const cabe = (izq ? lx - 6 : this.W - 6 - lx) - chk;
    c.textAlign = izq ? 'right' : 'left'; c.textBaseline = 'alphabetic';
    c.font = letra(13, it.visual === 'EN_CURSO' ? 600 : hecho ? 500 : 400);
    let t = it.nombre;
    const suf = it.visual === 'NO_APLICA' ? this.sufNoAplica(c, t, it, cabe) : it.espera ? ` · espera a ${it.espera}` : it.esperando ? ' · generándose' : '';
    if (suf && c.measureText(t + suf).width <= cabe) t += suf;
    t = recortar(c, t, cabe);
    c.fillStyle = it.visual === 'FALLO' ? C.mal : it.esperando ? C.alerta : it.visual === 'NO_APLICA' ? C.tenue : it.visual === 'PENDIENTE' ? C.mf : C.fg;
    c.fillText(t, lx, py + 4.5);
    if (hecho) { const tw = c.measureText(t).width; check(c, izq ? lx - tw - 17 : lx + tw + 6, py, 10, azul); }
  }

  /** Lo de abajo: el libro mayor del sistema administrativo o la hoja del kit, con sus tres renglones que se dibujan. */
  private dibujarAbajo(c: CanvasRenderingContext2D, it: Item, ahora: number, forma: 'libro' | 'hoja'): void {
    const C = this.pal;
    const azul = C.acento;
    const { x, y } = it;
    const hecho = HECHO(it.visual);
    const on = hecho || it.visual === 'EN_CURSO';
    const falla = it.visual === 'FALLO';
    c.save();
    c.strokeStyle = falla ? C.mal : on ? azul : C.tenue; c.lineWidth = 1.4;
    if (forma === 'libro') redondeado(c, x - 11, y - 12, 22, 24, 4);
    else { c.beginPath(); c.moveTo(x - 10, y - 13); c.lineTo(x + 4, y - 13); c.lineTo(x + 10, y - 7); c.lineTo(x + 10, y + 13); c.lineTo(x - 10, y + 13); c.closePath(); }
    if (hecho) { c.fillStyle = alfa(azul, forma === 'libro' ? 0.14 : 0.16); c.fill(); }
    c.stroke(); c.restore();
    const lp = hecho ? 1 : it.visual === 'EN_CURSO' ? (this.reducir ? 1 : Math.min(1, (ahora - it.t0) / MOTOR.dur)) : 0;
    c.strokeStyle = azul; c.lineWidth = 1.2;
    for (let i = 0; i < 3; i++) {
      const q = Math.max(0, Math.min(1, lp * 3 - i));
      if (q <= 0) continue;
      const yy = forma === 'libro' ? y - 5 + i * 5 : y - 4 + i * 6;
      c.beginPath(); c.moveTo(x - 6, yy); c.lineTo(x - 6 + 12 * q, yy); c.stroke();
    }
    // el nombre, sin pisar al de la derecha ni salirse del lienzo
    const vecino = this.items.filter((i) => i.lado >= 2 && i.x > x).reduce((m, i) => Math.min(m, i.x - 16), this.W - 6);
    c.textAlign = 'left'; c.textBaseline = 'alphabetic';
    c.font = letra(13, it.visual === 'EN_CURSO' ? 600 : hecho ? 500 : 400);
    c.fillStyle = falla ? C.mal : it.visual === 'NO_APLICA' ? C.tenue : on ? C.fg : C.mf;
    const chk = hecho ? 18 : 0;
    const cabe = vecino - (x + 20) - chk;
    let t = it.nombre;
    const suf = it.visual === 'NO_APLICA' ? this.sufNoAplica(c, t, it, cabe) : it.espera ? ` · espera a ${it.espera}` : '';
    if (suf && c.measureText(t + suf).width <= cabe) t += suf;
    t = recortar(c, t, cabe);
    c.fillText(t, x + 20, y + 2);
    if (hecho) check(c, x + 26 + c.measureText(t).width, y - 2, 10, azul);
    punto(c, x + 23, y + 16, 3, azul);
    this.rotuloGrupo(c, `${it.grupoNombre} · ${this.textoCuenta(it.lado)}`, x + 31, y + 20, 'left', vecino - (x + 31));
  }

  /** Lo que se escribe tras el nombre de un paso que no aplica: el porqué corto si el núcleo lo dice («apagada en este ambiente»), si cabe; si no, «no aplica». */
  private sufNoAplica(c: CanvasRenderingContext2D, nombre: string, it: Item, cabe: number): string {
    const d = (it.detalle || '').toLowerCase();
    const cand = /apagad/.test(d) ? ' · apagada en este ambiente' : /acr[oó]nimo/.test(d) ? ' · sin acrónimo' : /entra por/.test(d) ? ' · el portal entra por la dirección general' : '';
    if (cand && c.measureText(nombre + cand).width <= cabe) return cand;
    return ' · no aplica';
  }

  /** Pasos que no aplican o están en curso, con el texto que el núcleo les dio (el lienzo lo recorta; aquí se lee entero). */
  private avisosCache: { clave: string; nombre: string; estado: string; texto: string; alerta?: boolean }[] = [];
  private avisosLlave = '';
  get avisos(): { clave: string; nombre: string; estado: string; texto: string; alerta?: boolean }[] {
    const l: { clave: string; nombre: string; estado: string; texto: string; alerta?: boolean }[] = this.items
      .filter((i) => (i.visual === 'NO_APLICA' || i.visual === 'EN_CURSO') && !!i.detalle)
      .map((i) => ({ clave: i.clave, nombre: i.nombre, estado: i.visual === 'NO_APLICA' ? 'No aplica' : i.esperando ? 'Esperando' : 'En curso', texto: i.detalle as string, alerta: !!i.esperando }));
    // La entrega de accesos no sale mientras la dirección propia espera a Google.
    const espera = this.items.some((i) => i.clave === 'DIRECCION_PROPIA' && i.real === 'ESPERANDO');
    const ent = this.items.find((i) => i.clave === 'ENTREGA_ACCESOS' && i.real === 'PENDIENTE');
    if (espera && ent) l.push({ clave: ent.clave, nombre: ent.nombre, estado: 'Pendiente', texto: 'Es lo único que falta: sale cuando Google active la dirección propia. Las claves ya se pueden descargar en Credenciales, más abajo.' });
    const llave = JSON.stringify(l);
    if (llave !== this.avisosLlave) { this.avisosLlave = llave; this.avisosCache = l; }
    return this.avisosCache;
  }

  private rotuloGrupo(c: CanvasRenderingContext2D, t: string, x: number, y: number, al: CanvasTextAlign, max: number): void {
    c.font = letra(10.5, 600);
    const conEspacio = c as CanvasRenderingContext2D & { letterSpacing?: string };
    conEspacio.letterSpacing = '0.06em';
    c.fillStyle = this.pal.mf; c.textAlign = al; c.textBaseline = 'alphabetic';
    c.fillText(recortar(c, t.toUpperCase(), Math.max(40, max)), x, y);
    conEspacio.letterSpacing = '0px';
  }

  private dibujarTitular(c: CanvasRenderingContext2D, x: number, y: number, e: Titular): void {
    const C = this.pal;
    const max = this.W - 24;
    c.textBaseline = 'alphabetic';
    if (e.fin) {
      c.font = letra(22, 700); c.fillStyle = C.fg; c.textAlign = 'center';
      c.fillText(recortar(c, e.t, max), x, y);
    } else {
      c.font = letra(17, 600);
      const pre = e.chk ? 22 : e.punto ? 16 : 0;
      const t = recortar(c, e.t, max - pre);
      const tw = c.measureText(t).width;
      const x0 = x - (tw + pre) / 2;
      if (e.chk) check(c, x0, y - 6, 12, C.acento, 2);
      else if (e.punto) punto(c, x0 + 4, y - 6, 4, C.acento);
      c.fillStyle = e.color; c.textAlign = 'left'; c.fillText(t, x0 + pre, y);
    }
    if (e.sub) { c.font = letra(12.5); c.fillStyle = C.mf; c.textAlign = 'center'; c.fillText(recortar(c, e.sub, max), x, y + 22); }
  }

  // ─── los efectos (los de la maqueta en modo simple) ───────────────────────

  private nueva(p: Partial<Particula> & Pick<Particula, 't' | 'x' | 'y' | 'color'>): Particula {
    return { life: 0, r0: 0, r1: 0, dur: 1000, w: 1, vx: 0, vy: 0, rot: 0, vr: 0, s: 4, max: 1000, ph: 0, ...p };
  }

  private onda(x: number, y: number, color: string, r0: number, r1: number, dur: number, w = 1.5, delay = 0): void {
    if (this.reducir) return;
    this.parts.push(this.nueva({ t: 'ring', x, y, color, r0, r1, dur, w, life: -delay }));
    this.pedirCuadro();
  }

  /** La `fiesta` simple: un anillo fino que se abre detrás del comercio y ~90 papelitos celestes y ámbar que caen ~3 s. */
  private fiesta(x: number, y: number): void {
    if (this.reducir) return;
    this.onda(x, y, this.pal.acento, 62, 96, MOTOR.anillo, 1);
    const marca = [this.pal.acento, this.pal.ambar];
    for (let i = 0; i < 90; i++) {
      this.parts.push(this.nueva({
        t: 'conf', x: Math.random() * this.W, y: -10 - Math.random() * this.H * 0.5, vx: (Math.random() - 0.5) * 0.8, vy: 0.9 + Math.random() * 1.2,
        rot: Math.random() * 6.3, vr: (Math.random() - 0.5) * 0.2, color: marca[i % 2], s: 3.5 + Math.random() * 3.5,
        max: 2600 + Math.random() * 1000, ph: Math.random() * 6.3,
      }));
    }
  }

  private particulas(c: CanvasRenderingContext2D, dt: number): void {
    const k = dt / 16.7;
    this.parts = this.parts.filter((p) => {
      p.life += dt;
      if (p.life < 0) return true;
      if (p.t === 'ring') {
        const q = p.life / p.dur;
        if (q >= 1) return false;
        c.save(); c.globalAlpha = (1 - q) * 0.9; c.strokeStyle = p.color; c.lineWidth = p.w;
        c.beginPath(); c.arc(p.x, p.y, p.r0 + (p.r1 - p.r0) * suave(q), 0, 7); c.stroke(); c.restore();
        return true;
      }
      if (p.life >= p.max) return false;
      const q = p.life / p.max;
      p.vy += 0.018 * k; p.x += (p.vx + Math.sin(p.life / 260 + p.ph) * 0.7) * k; p.y += p.vy * k; p.rot += p.vr * k;
      c.save(); c.globalAlpha = q > 0.8 ? (1 - q) / 0.2 : 1; c.translate(p.x, p.y); c.rotate(p.rot); c.fillStyle = p.color;
      c.fillRect(-p.s / 2, -p.s / 4, p.s, (p.s / 2) * (0.35 + Math.abs(Math.sin(p.life / 170 + p.ph)) * 0.65)); c.restore();
      return true;
    });
  }

  // ─── el modo demo: un «servidor» de mentira y el mismo sondeo de 500 ms ────

  private arrancarDemo(): void {
    this.pararDemo();
    const ya = () => new Date().toISOString();
    const paso = (clave: string, nombre: string, estado: EstadoPaso = 'PENDIENTE'): EscenaPaso => ({ clave, nombre, estado, detalle: null, error: null, enlace: null, at: null, intentos: 0 });
    const cod = this.comercio?.codigo || 'VM';
    const datos: Record<string, string> = {
      COMERCIO: `${this.comercio?.nombre || 'VMagic Shop'} · ${cod}`, CONTACTOS: '3 · administración, finanzas, técnico',
      CONDICIONES: '7 días · 3 % · USD 1 por uso', DOCUMENTOS: '6 documentos', FICHA: 'aprobada', USUARIO_PORTAL: `${cod.toLowerCase()}.portal`,
      CUENTA_BANCARIA: 'terminada en 4821', PROVEEDOR: cod, DEPARTAMENTOS: 'Actividad · Odoo, 7 rutas', LLAVE: `prefijo ${cod.toLowerCase()}_live_…`,
      CREDENCIAL: `odoo-${cod.toLowerCase()}`, VERIFICACION: '7 de 7 rutas activas',
      ALTA_ADMINISTRATIVO: 'Simulada: el módulo del sistema administrativo todavía no existe', KIT_BIENVENIDA: 'PDF · 3 capítulos',
    };
    const errores: Record<string, string> = { CUENTA_BANCARIA: 'El número tiene 19 dígitos y deben ser 20.', LLAVE: 'La pasarela no respondió en 30 s.' };
    const servidor: EscenaPuesta = {
      estado: 'EN_CURSO', iniciada_at: ya(),
      grupos: [
        { clave: 'COMERCIO', nombre: 'Creación del comercio', estado: 'EN_CURSO', pasos: [
          paso('COMERCIO', 'Comercio'), paso('CONTACTOS', 'Contactos'), paso('CONDICIONES', 'Condiciones v1 aprobadas'),
          paso('DOCUMENTOS', 'Documentos en la ficha'), paso('FICHA', 'Ficha aprobada'), paso('USUARIO_PORTAL', 'Usuario del portal'),
          paso('CUENTA_BANCARIA', 'Cuenta bancaria principal'), paso('TIENDA_CAJA', 'Tienda y caja', 'NO_APLICA'),
        ] },
        { clave: 'PASARELA', nombre: 'Pasarela', estado: 'EN_CURSO', pasos: [
          paso('PROVEEDOR', 'Comercio en la pasarela (proveedor)'), paso('DEPARTAMENTOS', 'Departamento por modelo'),
          paso('LLAVE', 'Llave de API'), paso('CREDENCIAL', 'Credencial del integrador'), paso('VERIFICACION', 'Verificación en la pasarela'),
        ] },
        { clave: 'ADMINISTRATIVO', nombre: 'Sistema administrativo', estado: 'EN_CURSO', pasos: [paso('ALTA_ADMINISTRATIVO', 'Alta en el sistema administrativo')] },
        { clave: 'ENTREGA', nombre: 'Entrega', estado: 'EN_CURSO', pasos: [paso('KIT_BIENVENIDA', 'Kit de bienvenida')] },
      ],
    };
    this.demoServidor = servidor;
    const todos = (servidor.grupos || []).flatMap((g) => g.pasos);
    const buscar = (k: string) => todos.find((x) => x.clave === k);
    const necesita = (k: string): string[] => (DEPENDE[k] || []).flatMap((d) => (d.startsWith('*')
      ? (servidor.grupos || []).filter((g) => g.clave === d.slice(1)).flatMap((g) => g.pasos.map((x) => x.clave)) : [d]));
    const listo = (k: string) => { const x = buscar(k); return !x || x.estado === 'VERIFICADO' || x.estado === 'NO_APLICA'; };
    const falla = this.demoFalla === 'cuenta' ? 'CUENTA_BANCARIA' : this.demoFalla === 'llave' ? 'LLAVE' : null;
    const reintentados = new Set<string>();
    const copia = (): EscenaPuesta => JSON.parse(JSON.stringify(servidor));
    const empujar = () => this.zone.run(() => this.aplicar(copia()));
    let corriendo = false;
    // Como el núcleo: corre en orden lo que tiene lo suyo listo; lo que espera a algo que falló queda PENDIENTE.
    const correr = () => {
      corriendo = true;
      const siguiente = todos.find((x) => x.estado === 'PENDIENTE' && !x.detalle && necesita(x.clave).every(listo));
      if (!siguiente) {
        todos.filter((x) => x.estado === 'PENDIENTE').forEach((x) => {
          const f = necesita(x.clave).map(buscar).find((d) => d && !listo(d.clave));
          x.detalle = `Espera a «${f ? f.nombre : 'Comercio'}».`;
        });
        servidor.estado = todos.some((x) => x.estado === 'FALLO' || x.estado === 'PENDIENTE') ? 'FALLO' : 'OPERATIVA';
        corriendo = false;
        if (servidor.estado === 'OPERATIVA') {
          const k = buscar('KIT_BIENVENIDA');
          if (k) k.enlace = 'data:application/pdf;base64,JVBERi0xLjQKJSBLaXQgZGUgYmllbnZlbmlkYSAoZGVtbykK';
          this.demoTimers.push(window.setTimeout(() => { this.pararDemo(); this.reiniciarEscena(); this.arrancarDemo(); }, 9000));
        }
        empujar();
        return;
      }
      siguiente.estado = 'EN_CURSO'; siguiente.at = ya(); siguiente.intentos = (siguiente.intentos || 0) + 1;
      this.demoTimers.push(window.setTimeout(() => {
        if (siguiente.clave === falla && !reintentados.has(siguiente.clave)) { siguiente.estado = 'FALLO'; siguiente.error = errores[siguiente.clave] || 'No se pudo.'; }
        else { siguiente.estado = 'VERIFICADO'; siguiente.detalle = datos[siguiente.clave] || null; siguiente.error = null; }
        siguiente.at = ya();
        this.demoTimers.push(window.setTimeout(correr, 60 + Math.round(Math.random() * 120)));
      }, 480 + Math.round(Math.random() * 260)));
    };
    // Reintentar en el demo: ese paso vuelve a PENDIENTE, lo que esperaba por él se destraba, y corre de nuevo.
    this.demoReintentar = (clave: string) => {
      const x = buscar(clave);
      if (!x || x.estado !== 'FALLO') return;
      reintentados.add(clave);
      x.estado = 'PENDIENTE'; x.error = null; x.detalle = null;
      todos.filter((y) => y.estado === 'PENDIENTE').forEach((y) => { y.detalle = null; });
      servidor.estado = 'EN_CURSO'; servidor.iniciada_at = ya();
      empujar();
      if (!corriendo) this.demoTimers.push(window.setTimeout(correr, 300));
    };
    this.aplicar(copia());
    this.demoTimers.push(window.setTimeout(correr, 700));
    this.demoTimers.push(window.setInterval(empujar, 500));
  }

  private pararDemo(): void {
    this.demoTimers.forEach((t) => { window.clearTimeout(t); window.clearInterval(t); });
    this.demoTimers = [];
    this.demoServidor = null;
    this.demoReintentar = null;
  }

  private reiniciarEscena(): void {
    this.items = []; this.porClave.clear(); this.cola = []; this.primera = true; this.estadoReal = ''; this.cortada = false;
    this.actual = null; this.actualHasta = 0; this.fase = 'corriendo'; this.tFin = 0; this.fiestaHecha = true; this.parts = [];
    this.inicioServidor = null; this.finServidor = null; this.libreDesde = 0;
    this.fallos = []; this.enlaceKit = null; this.anuncio = ''; this.rotulo = '';
  }
}

// ─── ayudantes de dibujo ─────────────────────────────────────────────────────

/** La curva de la maqueta (`ease`): entra y sale suave. */
function suave(t: number): number { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }

/** Un color `rgb(...)` del tema, con transparencia. */
function alfa(color: string, a: number): string {
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (!m) return color;
  const [r, g, b] = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, a))})`;
}

function punto(c: CanvasRenderingContext2D, x: number, y: number, r: number, color: string): void {
  c.fillStyle = color; c.beginPath(); c.arc(x, y, r, 0, 7); c.fill();
}

/** Un rectángulo redondeado (el `rr` de la maqueta), sin depender de `roundRect`. */
function redondeado(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, h / 2, w / 2);
  c.beginPath();
  c.moveTo(x + rr, y);
  c.arcTo(x + w, y, x + w, y + h, rr);
  c.arcTo(x + w, y + h, x, y + h, rr);
  c.arcTo(x, y + h, x, y, rr);
  c.arcTo(x, y, x + w, y, rr);
  c.closePath();
}

function check(c: CanvasRenderingContext2D, x: number, y: number, s: number, color: string, lw = 1.7): void {
  c.save();
  c.beginPath(); c.moveTo(x, y); c.lineTo(x + s * 0.35, y + s * 0.35); c.lineTo(x + s, y - s * 0.45);
  c.strokeStyle = color; c.lineWidth = lw; c.lineCap = 'round'; c.lineJoin = 'round'; c.stroke();
  c.restore();
}

/** Ningún texto se sale de su casilla: se mide y, si no cabe, se recorta con «…». */
function recortar(c: CanvasRenderingContext2D, texto: string, max: number): string {
  if (max <= 0) return '';
  if (c.measureText(texto).width <= max) return texto;
  let t = texto;
  while (t.length > 1 && c.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

function horaCorta(ms: number): string {
  return new Date(ms).toLocaleTimeString('es-VE', { hour: 'numeric', minute: '2-digit' });
}

function duracion(ms: number): string {
  const s = Math.max(1, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m} min ${r} s` : `${m} min`;
}
